import { useEffect, useState } from 'react';
import { useFetchFromServer } from 'core/ui/utils/useFetchFromServer';
import { useFetchObjectFromServer } from 'core/ui/utils/useFetchObjectFromServer';
import { simpleClient, EntityWithId } from 'services/apiGatewayClient';
import { ITemplate } from 'entities/template/template';

export interface PreviewPayload {
  channel: string;
  template: string;
}

export const useTemplates = (enabled: boolean, siteId: string) => {
  const [url, setUrl] = useState<string>(null);

  const {
    state: { data, status },
    setUrl: fetchTemplate,
  } = useFetchFromServer<ITemplate>();

  const valid = enabled && siteId && siteId !== '';

  const templates: ITemplate[] = valid && data && data.length ? data : [];

  useEffect(() => {
    if (valid) {
      setUrl(`/sites/${siteId}/templates/new`);
    } else {
      setUrl(null);
    }
  }, [siteId, valid]);

  useEffect(() => {
    if (valid) {
      fetchTemplate(url);
    }
  }, [fetchTemplate, url, valid]);

  return {
    templates,
    status: valid ? status : 'resolved',
  };
};

/**
 * Gets the uncompiled html for a selected template id
 */
export const useTemplate = (enabled: boolean, siteId: string, templateId: string) => {
  const [url, setUrl] = useState<string>(null);

  const {
    state: { data, status },
    setUrl: fetchTemplate,
  } = useFetchObjectFromServer<PreviewPayload>();

  const valid = enabled && siteId && siteId !== '' && templateId && templateId !== '';

  const template: PreviewPayload = valid && data?.entity ? data.entity : undefined;

  useEffect(() => {
    if (valid) {
      setUrl(`/sites/${siteId}/templates/new/${templateId}`);
    } else {
      setUrl(null);
    }
  }, [siteId, templateId, valid]);

  useEffect(() => {
    if (valid) {
      fetchTemplate(url);
    }
  }, [fetchTemplate, url, valid]);

  return {
    template,
    status: valid ? status : 'resolved',
  };
};

/**
 * Returns the compiled html for an email template.
 */
export function postEmailTemplate(templateHtml: string, siteId: string): Promise<EntityWithId<{ compiled: string }>> {
  const client = simpleClient<any>();

  // Add some default config values to the payload to ensure the template compiles
  const payload = {
    compilationContext: { unsubscribeUrl: 'fakeurl.com' },
    // Using the same hardcoded compilationId as the design gallery 🤷‍♂️
    linkConfig: [{ category: 'redirect', config: {}, kind: 'salecycle', sequence: 1 }],
    template: { type: 'body', value: templateHtml },
    channel: 'email',
    ids: {
      compilationId: 'b283a6cd-56e8-4f27-9f30-e87488098664',
      siteId: siteId,
      snapshotId: 'b283a6cd-56e8-4f27-9f30-e87488098664',
      machineGuid: 'b283a6cd-56e8-4f27-9f30-e87488098664',
    },
  };

  return client.post('/templates/compile', payload);
}
